/**
 * Cognito error → friendly, app-owned copy (FR-2.11). The in-app auth screens must
 * never surface a raw exception; every known Cognito error code maps to human copy
 * here, and anything unrecognized falls back to a generic message. Deliberately maps
 * both `UserNotFoundException` and `NotAuthorizedException` to the same "incorrect
 * username or password" copy so sign-in does not become a user-enumeration oracle.
 */

/** The shape `amazon-cognito-identity-js` errors expose (a `code`/`name` string). */
interface CognitoLikeError {
  code?: string;
  name?: string;
  message?: string;
}

export function mapAuthError(err: unknown): string {
  const e = (err ?? {}) as CognitoLikeError;
  const code = e.code ?? e.name ?? '';
  switch (code) {
    case 'UsernameExistsException':
      return 'That username is already taken. Try signing in instead.';
    case 'UserNotConfirmedException':
      return 'Your account is not verified yet — check your email for the code.';
    case 'CodeMismatchException':
      return 'That code is not right. Check it and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one.';
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      // Same copy for both so sign-in cannot be used to enumerate accounts.
      return 'Incorrect username or password.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Please wait a little while and try again.';
    case 'InvalidPasswordException':
      return 'That password does not meet the requirements: at least 8 characters, a number, and a lowercase letter.';
    case 'InvalidParameterException':
      return 'Please check the details you entered and try again.';
    case 'under_age':
      // COPPA 13+ age gate (FR-14.1, #41). Neutral, non-judgmental copy.
      return 'You must be at least 13 years old to create an account.';
    case 'PlusAddressNotAllowed':
      // Account-reuse abuse prevention (NFR-5.7, #171): plus-addressed aliases rejected.
      return 'Email aliases with a "+" are not allowed — use your primary email address.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
